import { useCallback, useEffect, useRef, useState } from 'react';

/** Matches the auto-dismiss used by PublishWorkflow's local toast. */
export const TOAST_DURATION_MS = 4000;

/**
 * Transient single-message toast state: `showToast` replaces any message on
 * screen and restarts the dismiss timer; the timer is cleared on unmount.
 */
export function useToast(durationMs: number = TOAST_DURATION_MS) {
  const [message, setMessage] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const showToast = useCallback((next: string) => {
    clearTimer();
    setMessage(next);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setMessage(null);
    }, durationMs);
  }, [clearTimer, durationMs]);

  useEffect(() => clearTimer, [clearTimer]);

  return { message, showToast };
}
